/**
 * Career Service
 *
 * API service for opportunities and CV management.
 */

import { TonnieClient } from '../client/base';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface Opportunity {
  id: string;
  title: string;
  company: string;
  companyLogo?: string;
  location: string;
  country: string;
  category: string;
  type: 'full-time' | 'part-time' | 'contract' | 'internship';
  remote: boolean;
  salary?: {
    min: number;
    max: number;
    currency: string;
  };
  description: string;
  requirements: string[];
  benefits: string[];
  postedAt: string;
  expiresAt?: string;
  isSaved?: boolean;
  hasApplied?: boolean;
}

export interface OpportunityFilters {
  country?: string;
  category?: string;
  remote?: boolean;
  type?: string;
}

export interface CVData {
  id: string;
  sections: {
    personalInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      location?: string;
    };
    summary?: string;
    experience: Array<{
      company: string;
      role: string;
      startDate: string;
      endDate?: string;
      description?: string;
    }>;
    education: Array<{
      institution: string;
      degree: string;
      field: string;
      startDate: string;
      endDate?: string;
    }>;
    skills: string[];
    languages: Array<{
      name: string;
      level: string;
    }>;
  };
  template: string;
  lastGeneratedAt?: string;
}

export interface GenerateCVInput {
  template?: string;
  sections?: string[];
  /** Target job title for CV tailoring */
  jobTitle?: string;
  /** Job description for AI-powered customization */
  jobDescription?: string;
  /** Target country for market-specific formatting */
  targetCountry?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class CareerService {
  constructor(private client: TonnieClient) {}

  /**
   * Get opportunities
   */
  async getOpportunities(
    filters?: OpportunityFilters
  ): Promise<{ opportunities: Opportunity[]; total: number }> {
    return this.client.get<{ opportunities: Opportunity[]; total: number }>(
      '/api/career/opportunities',
      { params: filters as Record<string, string | number | boolean | undefined> }
    );
  }

  /**
   * Get single opportunity
   */
  async getOpportunity(id: string): Promise<Opportunity> {
    return this.client.get<Opportunity>(`/api/career/opportunities/${id}`);
  }

  /**
   * Express interest in opportunity
   */
  async expressInterest(id: string, coverNote?: string): Promise<{ success: boolean }> {
    return this.client.post<{ success: boolean }>(`/api/career/opportunities/${id}/interest`, {
      coverNote,
    });
  }

  /**
   * Save opportunity for later
   */
  async saveOpportunity(id: string): Promise<{ success: boolean }> {
    return this.client.post<{ success: boolean }>(`/api/career/opportunities/${id}/save`);
  }

  /**
   * Get saved opportunities
   */
  async getSavedOpportunities(): Promise<Opportunity[]> {
    return this.client.get<Opportunity[]>('/api/career/opportunities/saved');
  }

  /**
   * Get CV data
   */
  async getCV(): Promise<CVData> {
    return this.client.get<CVData>('/api/career/cv');
  }

  /**
   * Generate CV from profile
   */
  async generateCV(input?: GenerateCVInput): Promise<CVData> {
    return this.client.post<CVData>('/api/career/cv/generate', input || {});
  }

  /**
   * Download generated CV
   */
  async downloadCV(format = 'pdf'): Promise<{ downloadUrl: string }> {
    return this.client.get<{ downloadUrl: string }>('/api/career/cv/download', {
      params: { format },
    });
  }
}

export function createCareerService(client: TonnieClient): CareerService {
  return new CareerService(client);
}
